import type { SpanJSON, TransactionEvent } from '@sentry/core';
import type * as Sentry from '@sentry/nextjs';
import { AppError, hasAppErrorInCauseChain } from '@/libs/error/error';
import { AuthErrorCode, ClientErrorCode, TimeoutErrorCode } from '@/libs/error/error.codes';
import { ErrorCategory, ErrorService } from '@/libs/error/error.types';
import { HttpStatusCode } from '@/libs/http/http.types';
import {
  EMAIL_PATTERN,
  EMAIL_REDACTED,
  HOMESERVER_EVENT_STREAM_SUBSCRIBE_OPERATION,
  LOCKS_UNLOCK_POLL_CALL_SITES,
  NEXUS_POST_TAGS_PATH_PATTERN,
  PHONE_PATTERN,
  PHONE_REDACTED,
  PUBKY_COMPACT_URI_PATTERN,
  PUBKY_HTTP_HOST_PATTERN,
  PUBKY_IDENTIFIER_KEYS,
  PUBKY_REDACTED,
  PUBKY_URI_PATTERN,
  RAW_PUBKY_PATTERN,
  SENSITIVE_CONTEXT_KEYS,
  SENSITIVE_VALUE_REDACTED,
} from './sentry.constants';

function normalizeContextKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isSensitiveContextKey(key: string): boolean {
  const normalizedKey = normalizeContextKey(key);
  return SENSITIVE_CONTEXT_KEYS.has(normalizedKey) || PUBKY_IDENTIFIER_KEYS.has(normalizedKey);
}

function scrubSensitiveString(value: string): string {
  return value
    .replace(PUBKY_URI_PATTERN, PUBKY_REDACTED)
    .replace(PUBKY_HTTP_HOST_PATTERN, PUBKY_REDACTED)
    .replace(PUBKY_COMPACT_URI_PATTERN, PUBKY_REDACTED)
    .replace(RAW_PUBKY_PATTERN, PUBKY_REDACTED)
    .replace(EMAIL_PATTERN, EMAIL_REDACTED)
    .replace(PHONE_PATTERN, PHONE_REDACTED);
}

function getEndpointPath(endpoint: unknown): string | null {
  if (typeof endpoint !== 'string') return null;

  try {
    return new URL(endpoint).pathname;
  } catch {
    return endpoint.startsWith('/') ? endpoint.split('?')[0] : null;
  }
}

type AppErrorDropRule = {
  name: string;
  reason: string;
  matches: (error: AppError) => boolean;
};

function matchesEndpointPath(error: AppError, pattern: RegExp): boolean {
  const endpointPath = getEndpointPath(error.context?.endpoint);
  return endpointPath ? pattern.test(endpointPath) : false;
}

const LOCKS_UNLOCK_POLL_CALL_SITE_SET: ReadonlySet<unknown> = new Set(Object.values(LOCKS_UNLOCK_POLL_CALL_SITES));

/**
 * Expected-error drop rules (ADR-0015 §5.1 Challenge 3).
 *
 * `Err.*` captures at construction time, before any `catch` runs, so this list is the ONLY place an
 * expected `AppError` can be kept out of Sentry. Downgrading a `Logger` level, re-tagging a category,
 * or `preventDefault()` on an unhandled rejection all happen after the event has been sent.
 *
 * Every rule must match on the shape the error pipeline actually emits (`service` / `operation` /
 * `code` / `context.statusCode`) and be covered by a test that routes a fixture through that pipeline
 * (`safeFetch`, homeserver `handleError`, ...) rather than a hand-built `new AppError(...)`.
 */
const APP_ERROR_DROP_RULES: AppErrorDropRule[] = [
  {
    name: 'unusable-saved-grant',
    reason:
      'Known SDK restore outcomes require sign-in or finish pending cleanup (PR #2614). ' +
      'Unreadable versions, storage failures and generic authentication failures remain reportable.',
    matches: (error) =>
      error.service === ErrorService.Homeserver &&
      error.operation === 'restoreGrant' &&
      error.category === ErrorCategory.Auth &&
      error.code === AuthErrorCode.SESSION_EXPIRED &&
      ['missing_local_grant', 'remote_logout_completed', 'invalid_grant'].includes(String(error.context?.reason)),
  },
  {
    name: 'nexus-post-tags-404',
    reason: 'Low-value post-tags telemetry; Nexus retry behavior is preserved and other Nexus errors stay reportable.',
    matches: (error) =>
      error.service === ErrorService.Nexus &&
      error.operation === 'fetchNexus' &&
      error.code === ClientErrorCode.NOT_FOUND &&
      error.context?.statusCode === HttpStatusCode.NOT_FOUND &&
      matchesEndpointPath(error, NEXUS_POST_TAGS_PATH_PATTERN),
  },
  {
    name: 'rate-limit-repeat-reports',
    reason:
      'A 429 is one server-side throttling window, not one failure per request: a throttled client keeps issuing queries and the ' +
      'query client retries a 429 once after the 429 backoff, so reporting every attempt produced ~8.7k events for a handful of ' +
      'windows (PUBKY-APP-B3 on /hot, PUBKY-APP-9X on /home). httpStatusCodeToError reports the first 429 per service/operation per ' +
      'window and tags the repeats with context.reportSuppressed, so throttling stays visible while the repeat events are dropped. ' +
      'Non-429 rate-limit errors carry no flag and stay reportable.',
    matches: (error) =>
      error.category === ErrorCategory.RateLimit &&
      error.context?.statusCode === HttpStatusCode.TOO_MANY_REQUESTS &&
      error.context?.reportSuppressed === true,
  },
  {
    name: 'locks-unlock-poll-repeat-reports',
    reason:
      'While a reader waits in the Pay to Unlock modal, usePayToUnlock polls the verification task every 3 s and the ' +
      'Paykit link every 1 s, so a Lock Server outage built one AppError per tick, ~240 events per 3-minute wait (issue ' +
      '#2641). The loops back off and tag every failure after the first of a streak with context.reportSuppressed, ' +
      'so the first failure of each outage stays reportable; untagged reads (the open-time lookup, the purchase ' +
      'resume) and other Locks operations are untouched. A streak does not tell failures apart: a different failure ' +
      'during one (a response the schema rejects after a run of 503s, say) is tagged too until the streak ends on an ' +
      'answer, a return to the tab or a new wait, while a reader who opens the dialog afterwards reports it.',
    matches: (error) =>
      error.service === ErrorService.Locks &&
      LOCKS_UNLOCK_POLL_CALL_SITE_SET.has(error.context?.callSite) &&
      error.context?.reportSuppressed === true,
  },
  {
    name: 'aborted-requests',
    reason:
      'REQUEST_ABORTED is only produced by safeFetch when fetch rejects with an AbortError DOMException. When the ' +
      "caller's own AbortSignal fired (context.signalAborted) the cancellation is deliberate control flow — " +
      'navigation, teardown, long-poll reset — and every such caller handles the rejection (PUBKY-APP-3N/4F). ' +
      'Aborts with no signal (browser-driven) and AbortSignal.timeout() TimeoutErrors (mapped to a Network code) ' +
      'stay reportable.',
    matches: (error) => error.code === TimeoutErrorCode.REQUEST_ABORTED && error.context?.signalAborted === true,
  },
  {
    name: 'shop-listing-metadata-transport',
    reason:
      'Shop listing previews are best-effort enrichment (PR #2835, review 4292696485). safeFetch constructs transport ' +
      'errors before the service returns its generic preview, so an unavailable Shop otherwise creates an alert per crawl. ' +
      'Keep these connection/timeout failures in local logs; Shop readiness, cleanup, auth and payment operations, ' +
      'and non-transport errors on this operation remain reportable.',
    matches: (error) =>
      error.service === ErrorService.Shop &&
      error.operation === 'fetchListingMetadata' &&
      (error.category === ErrorCategory.Network || error.category === ErrorCategory.Timeout),
  },
  {
    name: 'pinakes-transport',
    reason:
      'External resource cards are optional enrichment of a link: on any failure the post shows the plain link ' +
      'preview. safeFetch constructs connection and timeout errors before that fallback runs, and they come mostly ' +
      'from the viewer (offline, an ad blocker, a CORS refusal, a stalled connection), so one unreachable Pinakes ' +
      'otherwise creates events for every distinct link on every feed view, each carrying the post link in its ' +
      'context url. Pinakes HTTP errors (4xx other than 404, 5xx, malformed bodies) and every other service remain ' +
      'reportable.',
    matches: (error) =>
      error.service === ErrorService.Pinakes &&
      (error.category === ErrorCategory.Network || error.category === ErrorCategory.Timeout),
  },
  {
    name: 'homeserver-event-stream-connect',
    reason:
      'The mute-list SSE subscribe drops routinely (homeserver deploys, idle timeouts, mobile backgrounding) and ' +
      'MuteListSyncCoordinator reconnects with backoff by design. Connect failures reach here as ' +
      'handleError → httpStatusCodeToError(500) → Err.server(INTERNAL_ERROR) tagged with the subscribe operation ' +
      '(PUBKY-APP-11/1Y/6G/CX), or as Err.network(CONNECTION_FAILED) when the SDK cannot resolve the homeserver ' +
      "from PKARR first ('PkarrError', @synonymdev/pubky >= 0.10). Auth/validation failures on the same operation and every other Homeserver " +
      'operation stay reportable, and the coordinator reports a persistent outage once via the ' +
      "'muteListEventStreamExhausted' operation after MUTE_SYNC_STREAM_FAILURE_ALERT_THRESHOLD consecutive failures.",
    matches: (error) =>
      error.service === ErrorService.Homeserver &&
      error.operation === HOMESERVER_EVENT_STREAM_SUBSCRIBE_OPERATION &&
      (error.category === ErrorCategory.Server || error.category === ErrorCategory.Network),
  },
];

export function shouldDropAppErrorFromSentry(error: AppError): boolean {
  return APP_ERROR_DROP_RULES.some((rule) => rule.matches(error));
}

/**
 * Whether an exception reaching the SDK outside `captureAppError` must be dropped.
 *
 * `Err.*` decides at construction whether an AppError is captured (drop rules) and skips wrappers
 * whose cause chain already holds a captured AppError (once per chain). That decision only covers
 * the factory's own `captureException` call: the same object can reach the SDK again through
 * `globalHandlers` when a caller lets the promise reject unhandled, or through `app/error.tsx`.
 * Sentry's dedupe works per error object, not per cause chain, and a rule-dropped error was never
 * captured at all — so `beforeSend` re-applies both predicates to `hint.originalException`.
 *
 * Non-AppError exceptions are untouched: the once-per-chain contract only exists for `Err.*`.
 */
export function shouldDropCapturedExceptionFromSentry(originalException: unknown): boolean {
  if (!(originalException instanceof AppError)) return false;
  return shouldDropAppErrorFromSentry(originalException) || hasAppErrorInCauseChain(originalException.cause);
}

function isSensitiveFieldValue(parent: Record<string, unknown>, key: string): boolean {
  if (normalizeContextKey(key) !== 'value') return false;
  if (typeof parent.field !== 'string') return false;
  return isSensitiveContextKey(parent.field);
}

export function sanitizeForSentry(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === 'string') {
    return scrubSensitiveString(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForSentry(item, seen));
  }

  if (typeof value !== 'object' || value === null) {
    return value;
  }

  if (seen.has(value)) {
    return '[redacted: circular reference]';
  }
  seen.add(value);

  if (value instanceof Date) {
    return value;
  }

  const sanitized: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    sanitized[key] =
      isSensitiveContextKey(key) || isSensitiveFieldValue(value as Record<string, unknown>, key)
        ? SENSITIVE_VALUE_REDACTED
        : sanitizeForSentry(item, seen);
  }

  return sanitized;
}

/**
 * String-only deep walker for SDK-owned telemetry payloads (transactions, spans).
 *
 * Mutates `value` in place: every string descendant is replaced with the scrubbed string;
 * non-string scalars pass through unchanged. The same input reference is returned so callers
 * can keep their existing object identity (Sentry's `beforeSendTransaction` / `beforeSendSpan`
 * contracts expect a value to be returned, and aliased references in span/trace data must
 * observe the first-pass scrubbed value).
 *
 * Crucially, this walker does NOT apply key-based redaction. SDK-owned payloads use keys like
 * `name` (input attributes, browser/runtime/os/device contexts) for structural data, so the
 * keyed `sanitizeForSentry` walker would corrupt them. Use `sanitizeForSentry` only on
 * AppError-shaped attachments (`extra`, `user`, `error.context`).
 *
 * Arrays are handled before generic objects. The shared `seen` WeakSet guards against cycles
 * and ensures aliased subtrees aren't double-walked. On a re-visit the original reference is
 * returned unchanged — its strings were scrubbed during the first visit (mutate-in-place).
 */
function deepScrubTelemetryStrings<T>(value: T, seen: WeakSet<object>): T {
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const item = value[i];
      if (typeof item === 'string') {
        value[i] = scrubSensitiveString(item);
      } else if (item !== null && typeof item === 'object') {
        deepScrubTelemetryStrings(item, seen);
      }
    }
    return value;
  }

  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const item = obj[key];
    if (typeof item === 'string') {
      obj[key] = scrubSensitiveString(item);
    } else if (item !== null && typeof item === 'object') {
      deepScrubTelemetryStrings(item, seen);
    }
  }
  return value;
}

/**
 * Defensive PII filter — redacts user-provided identifiers across application-owned
 * event surfaces (messages, exception values, breadcrumb data, extras, and AppError context).
 *
 * SDK-side collection is already minimized by the `dataCollection` mapping in
 * `getSentryInitBase()` (no IP inference, no `user.ip_address`, no IP-bearing headers);
 * this hook is the second line of defense for application payloads we attach ourselves.
 */
export function scrubSensitiveData(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  event.message = event.message ? scrubSensitiveString(event.message) : event.message;

  // captureException populates event.exception.values[].value with the error message.
  // Unlike event.breadcrumbs, event.exception IS wrapped as { values: Exception[] } in-SDK.
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((exception) => {
      return {
        ...exception,
        value: exception.value ? scrubSensitiveString(exception.value) : exception.value,
      };
    });
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => {
      return {
        ...crumb,
        message: crumb.message ? scrubSensitiveString(crumb.message) : crumb.message,
        data: crumb.data ? (sanitizeForSentry(crumb.data) as Sentry.Breadcrumb['data']) : crumb.data,
      };
    });
  }

  if (event.contexts?.['error.context']) {
    event.contexts['error.context'] = sanitizeForSentry(event.contexts['error.context']) as Sentry.Context;
  }

  if (event.extra) {
    event.extra = sanitizeForSentry(event.extra) as Record<string, unknown>;
  }

  if (event.request) {
    event.request = sanitizeForSentry(event.request) as Sentry.ErrorEvent['request'];
  }

  if (event.user) {
    event.user = sanitizeForSentry(event.user) as Sentry.ErrorEvent['user'];
  }

  return event;
}

/**
 * Defensive PII filter for transaction events.
 *
 * Tracing payloads are SDK-owned. `event.transaction`, `event.request`, and the root span data
 * at `event.contexts.trace.data` carry user-controlled URL strings (pageload/navigation routes,
 * fetch URLs) where pubky URIs and homeserver hostnames land verbatim. We mutate in place via
 * the string-only walker so aliased references converge on the scrubbed value.
 *
 * `event.extra`, `event.user`, and `event.contexts['error.context']` are the AppError-shaped
 * carriers that may surface on transactions when `Sentry.setUser` / `setExtra` / scope contexts
 * have been set application-side; those are routed through the keyed `sanitizeForSentry`
 * walker (copy-on-write) so we redact whole values keyed by sensitive name (e.g. `email`,
 * `displayName`).
 *
 * SDK-structural contexts (`browser`, `runtime`, `os`, `device`) are deliberately not walked —
 * the keyed walker would clobber `name: 'Chrome'` / `name: 'node'`, and the string-only walker
 * adds no value there. `event.spans[]` is handled per-span by `beforeSendSpan` and must not be
 * mutated here. Absent fields are not created.
 */
export function scrubTransactionEvent(event: TransactionEvent): TransactionEvent {
  const seen = new WeakSet<object>();

  if (typeof event.transaction === 'string') {
    event.transaction = scrubSensitiveString(event.transaction);
  }

  if (event.request && typeof event.request === 'object') {
    deepScrubTelemetryStrings(event.request, seen);
  }

  if (
    event.contexts &&
    event.contexts.trace &&
    event.contexts.trace.data &&
    typeof event.contexts.trace.data === 'object'
  ) {
    deepScrubTelemetryStrings(event.contexts.trace.data, seen);
  }

  if (event.extra) {
    event.extra = sanitizeForSentry(event.extra) as typeof event.extra;
  }

  if (event.user) {
    event.user = sanitizeForSentry(event.user) as typeof event.user;
  }

  if (event.contexts?.['error.context']) {
    event.contexts['error.context'] = sanitizeForSentry(event.contexts['error.context']) as Sentry.Context;
  }

  // Tags are app-controlled operational labels; do not walk them as user payload.

  return event;
}

/**
 * Defensive PII filter for span events.
 *
 * Span `description` (often a URL) and `span.data` (containing keys such as `http.url`,
 * `url.full`, `http.target`, `db.statement`) are user-controlled string surfaces. Walked
 * via the string-only walker; never apply key-based redaction (the SDK uses structural
 * keys here that overlap with our keyed walker's sensitive set). Always return the same
 * span object — `SpanJSON` is non-nullable in the v10.51 contract.
 */
export function scrubSpanJson(span: SpanJSON): SpanJSON {
  const seen = new WeakSet<object>();

  if (typeof span.description === 'string') {
    span.description = scrubSensitiveString(span.description);
  }

  if (span.data && typeof span.data === 'object') {
    deepScrubTelemetryStrings(span.data, seen);
  }

  return span;
}
